import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, HostListener, PLATFORM_ID, inject} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map, Subscription } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AppIconComponent } from '@app/shared/icons/app-icon.component';
import { AuthService } from '@app/core/services/auth.service';
import { LoginPopupService } from '@app/core/services/login-popup.service';

@Component({
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatSidenavModule, AppIconComponent],
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})

export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private static readonly TRANSPARENT_PAGES = new Set([
    '/home',
    '/seller-guide',
    '/buyer-guide',
    '/home-estimate-page',
    '/zestimate',
  ]);

  private routerSubscription?: Subscription;

  // Data members
  desktopHeaderClass = 'desktop-header-transparent';
  mobileHeaderClass = 'mobile-header-transparent';
  buyMenuOpen: boolean = false;
  profileMenuOpen: boolean = false;
  sellMenuOpen: boolean = false;
  mobileNavOpen: boolean = false;
  openedModal: boolean = false;
  readonly isLoggedIn$ = this.auth.isLoggedIn.asObservable();
  readonly isLoggedOut$ = this.isLoggedIn$.pipe(map((isLoggedIn) => !isLoggedIn));
  isAdmin: Boolean = false;
  sideNavConfig: {
    from: "right",
    
  }
  routes = [];

  closeMobileNav(drawer: { close: () => void }): void {
    this.setMobileNavOpen(false);
    drawer.close();
  }

  toggleMobileNav(drawer: { opened: boolean; open: () => void; close: () => void }): void {
    if (drawer.opened) {
      this.closeMobileNav(drawer);
      return;
    }

    this.setMobileNavOpen(true);
    drawer.open();
  }

  onMobileNavOpenedChange(opened: boolean): void {
    this.setMobileNavOpen(opened);
    if (!opened) {
      this.resetMobileMenus();
    }
  }

  private setMobileNavOpen(open: boolean): void {
    this.mobileNavOpen = open;
    this.syncMobileNavScrollLock(open);
  }

  private syncMobileNavScrollLock(open: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.document.body.classList.toggle('mobile-nav-open', open);
    this.document.documentElement.classList.toggle('mobile-nav-open', open);
  }

  private resetMobileMenus(): void {
    this.buyMenuOpen = false;
    this.profileMenuOpen = false;
    this.sellMenuOpen = false;
  }

  constructor(
    public auth: AuthService,
    public loginPopup: LoginPopupService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.isAdmin = this.auth.isAdmin;
    this.auth.isLoggedIn.subscribe(() => {
      this.isAdmin = this.auth.isAdmin;
    });
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.updateHeaderClasses());
  }

  ngAfterViewInit() {
    this.updateHeaderClasses();
  }

  ngOnDestroy() {
    this.syncMobileNavScrollLock(false);
    this.routerSubscription?.unsubscribe();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.updateHeaderClasses();
  }

  private normalizePath(url: string): string {
    const path = (url.split('?')[0] || '/').replace(/\/$/, '') || '/';
    return path === '/' ? '/home' : path;
  }

  private isTransparentPage(): boolean {
    return HeaderComponent.TRANSPARENT_PAGES.has(this.normalizePath(this.router.url));
  }

  private updateHeaderClasses() {
    const transparentPage = this.isTransparentPage();
    if (!isPlatformBrowser(this.platformId)) {
      this.desktopHeaderClass = transparentPage ? 'desktop-header-transparent' : 'desktop-header';
      this.mobileHeaderClass = transparentPage ? 'mobile-header-transparent' : 'mobile-header';
      return;
    }

    const transparent = transparentPage && window.pageYOffset <= 44;
    this.desktopHeaderClass = transparent ? 'desktop-header-transparent' : 'desktop-header';
    this.mobileHeaderClass = transparent ? 'mobile-header-transparent' : 'mobile-header';
  }

  toggleBuyMenu(): void {
    this.buyMenuOpen = !this.buyMenuOpen;
  }

  toggleProfileMenu(): void {
    this.profileMenuOpen = !this.profileMenuOpen;
  }
  logout() {
    this.auth.logout('/home');
  }
  openSignIn() {
    this.loginPopup.openSignIn();
  }
  openSignUp() {
    this.loginPopup.openSignUp();
  }
  toggleSellMenu(): void {
    this.sellMenuOpen = !this.sellMenuOpen;
  }
}